import React from "react";
import {
  Pencil,
  Trash2,
  Users,
  Building2,
  UserCheck,
  FileText,
  Download,
} from "lucide-react";
import MarkdownRenderer from "./MarkdownRenderer";
import { lightLogo } from "../../../assets/img/image";
import { toast } from "react-toastify";

const formatAnnouncementDate = (dateString) => {
  if (!dateString) return "Recently";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;

  const datePart = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

  const timePart = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  return `${datePart} · ${timePart}`;
};

const SCOPE_BADGES = {
  all_users: {
    label: "All Users",
    icon: Users,
    classes: "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-800",
  },
  all_company: {
    label: "All Companies",
    icon: Building2,
    classes: "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800",
  },
  all_employee: {
    label: "All Employees",
    icon: UserCheck,
    classes: "bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300 border-purple-200 dark:border-purple-800",
  },
};

const AnnouncementCard = ({ announcement, onEdit, onDelete }) => {
  const scopeInfo = SCOPE_BADGES[announcement.scope] || {
    label: "All Users",
    icon: Users,
    classes: "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-800",
  };
  const ScopeIcon = scopeInfo.icon;

  const authorName =
    announcement.author?.name || announcement.authorName || "Super Admin";
  const authorRole =
    announcement.author?.role || announcement.authorRole || "Super Admin";
  const authorAvatar =
    announcement.author?.avatar || announcement.authorAvatar;

  const handleDownloadAttachment = () => {
    if (announcement.attachment?.url) {
      const link = document.createElement("a");
      link.href = announcement.attachment.url;
      link.download = announcement.attachment.name || "attachment";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      toast.info(`Downloading ${announcement.attachment?.name || "file"}...`);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700/80 shadow-xs hover:shadow-md transition-all p-5 sm:p-6 mb-4">
      {/* Top Header: Avatar + Author Info + Actions */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          {/* Logo / Avatar */}
          <div className="w-10 h-10 rounded-full bg-teal-50 dark:bg-teal-950/40 border border-teal-100 dark:border-teal-900/50 flex items-center justify-center p-1 shrink-0 overflow-hidden shadow-2xs">
            {authorAvatar ? (
              <img
                src={authorAvatar}
                alt={authorName}
                className="w-full h-full object-cover rounded-full"
              />
            ) : (
              <img
                src={lightLogo}
                alt="PeopleSync"
                className="w-full h-full object-contain"
              />
            )}
          </div>

          {/* Author Details */}
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-bold text-sm sm:text-base text-gray-900 dark:text-white">
                {authorName}
              </span>
              <span className="text-xs text-gray-400 dark:text-gray-500">·</span>
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                {formatAnnouncementDate(announcement.createdAt)}
              </span>
              <span
                className="w-2 h-2 rounded-full bg-[#05DAC3] shrink-0"
                title="Active broadcast"
              />
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">
              {authorRole}
            </p>
          </div>
        </div>

        {/* Top Right: Scope Badge & Edit/Delete Actions */}
        <div className="flex items-center gap-1.5 shrink-0">
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border mr-1 ${scopeInfo.classes}`}
          >
            <ScopeIcon className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">{scopeInfo.label}</span>
          </span>

          {onEdit && (
            <button
              type="button"
              onClick={() => onEdit(announcement)}
              className="p-1.5 text-gray-400 hover:text-[#00B4A6] dark:hover:text-[#05DAC3] hover:bg-teal-50 dark:hover:bg-teal-950/30 rounded-lg transition-colors"
              title="Edit announcement"
            >
              <Pencil className="w-4 h-4" />
            </button>
          )}

          {onDelete && (
            <button
              type="button"
              onClick={() => onDelete(announcement)}
              className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 rounded-lg transition-colors"
              title="Delete announcement"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* Announcement Title */}
      {announcement.title && (
        <h3 className="font-bold text-base sm:text-lg text-gray-900 dark:text-white mt-4 mb-2">
          {announcement.title}
        </h3>
      )}

      {/* Announcement Content (Rendered as Markdown) */}
      <div className="mt-1">
        <MarkdownRenderer content={announcement.message} />
      </div>

      {/* Announcement Image Attachment */}
      {announcement.image && (
        <div className="mt-4 rounded-xl overflow-hidden border border-gray-100 dark:border-gray-700/80 bg-gray-50 dark:bg-gray-900 flex justify-center">
          <img
            src={announcement.image}
            alt="Announcement Attachment"
            className="w-full max-h-96 object-contain rounded-xl"
            loading="lazy"
          />
        </div>
      )}

      {/* Announcement Document Attachment */}
      {announcement.attachment && (
        <div className="mt-4 flex items-center justify-between p-3.5 rounded-xl border border-teal-200 dark:border-teal-900/60 bg-teal-50/40 dark:bg-teal-950/20 hover:bg-teal-50 dark:hover:bg-teal-950/40 transition-colors">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-teal-600 text-white flex items-center justify-center font-bold text-xs shrink-0">
              <FileText className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white truncate">
                {announcement.attachment.name}
              </p>
              <p className="text-[11px] text-gray-500 dark:text-gray-400">
                {announcement.attachment.size} · {announcement.attachment.type || "DOC"}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleDownloadAttachment}
            className="p-1.5 text-teal-600 dark:text-teal-400 hover:bg-teal-100 dark:hover:bg-teal-900/50 rounded-lg transition-colors shrink-0"
            title="Download document"
          >
            <Download className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default AnnouncementCard;